import { useRef, useState } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { backdropClose } from '../../utils/backdropClose';

/**
 * Crop an image to a fixed aspect before upload — 4:5 for a coach's card
 * photo (the member card is portrait), 16:9 for a cover. Produces a JPEG no
 * wider than `maxWidth`, so uploads stay small.
 */
export default function ImageCropDialog({ src, aspect, title, maxWidth = 1200, onCancel, onConfirm, busy }: {
  src: string;
  aspect: number;
  title: string;
  maxWidth?: number;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
  busy?: boolean;
}) {
  const [crop, setCrop] = useState<Crop>();
  const [done, setDone] = useState<PixelCrop>();
  const img = useRef<HTMLImageElement>(null);

  const onLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerCrop(makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height), width, height));
  };

  const confirm = async () => {
    if (!img.current || !done) return;
    const sx = img.current.naturalWidth / img.current.width;
    const sy = img.current.naturalHeight / img.current.height;
    const srcW = done.width * sx;
    const srcH = done.height * sy;
    const scale = Math.min(1, maxWidth / srcW);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(srcW * scale);
    canvas.height = Math.round(srcH * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img.current, done.x * sx, done.y * sy, srcW, srcH, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/jpeg', 0.88));
    if (blob) onConfirm(blob);
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" {...backdropClose(onCancel)}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-black text-zinc-900">{title}</h3>
        <div className="rounded-xl bg-zinc-100 flex items-center justify-center min-h-[200px] overflow-hidden">
          <ReactCrop crop={crop} onChange={c => setCrop(c)} onComplete={c => setDone(c)} aspect={aspect} keepSelection>
            <img ref={img} src={src} alt="" onLoad={onLoad} className="max-h-[55vh] object-contain" />
          </ReactCrop>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl bg-zinc-100 text-zinc-700 text-sm font-bold hover:bg-zinc-200">Cancel</button>
          <button onClick={confirm} disabled={busy || !done} className="flex-1 py-2.5 rounded-xl bg-zinc-900 text-white text-sm font-bold hover:bg-black disabled:opacity-50">
            {busy ? 'Uploading…' : 'Use photo'}
          </button>
        </div>
      </div>
    </div>
  );
}
